import { Outlet } from '@tanstack/react-router';

import {
  SubsidiaryScopeContext,
  useResolvedSubsidiaryScope,
} from '~/features/platform/hooks/use-subsidiary-scope';

/**
 * Layout for the standalone upload-delivery flow (`/upload-delivery/*`).
 *
 * Deliberately outside the Integrations layout: the flow is a focused, multi-step task, and
 * the Integrations tab bar invited the operator to navigate away mid-upload — losing the
 * staged file and any un-committed step. The deliveries **list** stays on its Integrations
 * tab; only the creation flow lives here.
 *
 * Provides the same `?sc=` subsidiary scope the Integrations layout does, resolved by the
 * shared `useResolvedSubsidiaryScope`, so links in and out of the flow carry the scope
 * without changing subsidiary. No picker: the subsidiary is settled before the flow starts,
 * and switching it mid-upload would invalidate the connection and the staged file.
 */
export function UploadDeliveryLayout() {
  const scope = useResolvedSubsidiaryScope();

  return (
    // No card here — the stepper and step heading belong on the page surface, and the step's
    // own card is the only card. Wrapping this in a panel produced a card inside a card.
    // `overflow-hidden` + a full-height column lets the shell pin the stepper and scroll the
    // step body beneath it.
    <div className="bg-gradient-page flex h-full min-w-0 flex-col overflow-hidden">
      {/* Keyed by subsidiary so an out-of-band scope change resets the flow rather than
          leaving a file staged against the previous subsidiary's connector lane. */}
      <SubsidiaryScopeContext.Provider value={scope}>
        <div key={scope.id} className="flex min-h-0 min-w-0 flex-1 flex-col">
          <Outlet />
        </div>
      </SubsidiaryScopeContext.Provider>
    </div>
  );
}
